import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  MicrophoneIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import Button from "../common/Button";
import {
  PAGE_SIZE,
  formatDuration,
  MediaPlayer,
  SearchBar,
  Pagination,
  ModalListStates,
  DetailViewShell,
  ListViewShell,
  DetailField,
  ListItemButton,
} from "./MediaModalShared";

export interface PreviousUploadItem {
  id: string;
  // "case" — file came from a case_files row (audio uploaded into a case)
  // "form_submission" — file came from a form_submission_files row (audio uploaded for form fill)
  source?: "case" | "form_submission" | "library";
  // For "case": case id. For "form_submission": submission id. Kept named caseId for
  // backwards compatibility with existing callers.
  caseId: string;
  // For "case": the case number. For "form_submission": the form template name.
  caseNumber: string;
  parentLabel?: string;
  fileName: string;
  fileSize: string;
  fileType: string;
  fileUrl: string;
  createdAt: string;
  metadata: {
    audioType: "narrative" | "interaction";
    transcriptionUrl?: string;
    duration?: number;
    context?: string;
    speakers?: Array<{ name: string; role: string }>;
  };
}

interface PreviousUploadsMeta {
  count: number;
  offset: number;
  limit: number;
}

interface PreviousUploadsModalProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  onSelect: (item: PreviousUploadItem) => void;
  selectedFileIds?: string[];
}

const PreviousUploadsModal: React.FC<PreviousUploadsModalProps> = ({
  modalRef,
  onSelect,
  selectedFileIds = [],
}) => {
  const [files, setFiles] = useState<PreviousUploadItem[]>([]);
  const [meta, setMeta] = useState<PreviousUploadsMeta>({
    count: 0,
    offset: 0,
    limit: PAGE_SIZE,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [selectedItem, setSelectedItem] = useState<PreviousUploadItem | null>(
    null
  );
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [streamLoading, setStreamLoading] = useState(false);

  const fetchStreamUrl = async (fileId: string) => {
    setStreamLoading(true);
    setStreamUrl(null);
    try {
      const res = await axios.get(`/api/cases/files/${fileId}/stream`);
      setStreamUrl(res.data.streamUrl);
    } catch {
      setStreamUrl(null);
    } finally {
      setStreamLoading(false);
    }
  };

  const handleSelectItem = (item: PreviousUploadItem) => {
    setSelectedItem(item);
    fetchStreamUrl(item.id);
  };

  const fetchFiles = async (offset: number, searchTerm?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        limit: String(PAGE_SIZE),
        offset: String(offset),
      };
      if (searchTerm) params.search = searchTerm;

      // The Files library: reports, form fills, and files saved to Files.
      // Finished audio/video only; documents are picked from the Files page.
      params.kind = "audio,video";
      params.ready = "1";

      const res = await axios.get("/api/files", { params });
      setFiles(
        res.data.files.map(
          (f: any): PreviousUploadItem => ({
            id: f.id,
            source: f.source,
            caseId: f.parentId || "",
            caseNumber: f.source === "library" ? "Saved to Files" : f.parentLabel || "",
            parentLabel: f.source === "library" ? "Saved to Files" : f.parentLabel,
            fileName: f.fileName,
            fileSize: f.fileSize,
            fileType: f.fileType,
            fileUrl: f.fileUrl,
            createdAt: f.createdAt,
            metadata: {
              audioType: f.metadata.audioType || "narrative",
              transcriptionUrl: f.metadata.transcriptionUrl,
              duration: f.metadata.duration,
              context: f.metadata.context,
              speakers: f.metadata.speakers,
            },
          }),
        ),
      );
      setMeta(res.data.meta);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load previous uploads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;

    const observer = new MutationObserver(() => {
      if (dialog.open) {
        setSelectedItem(null);
        setStreamUrl(null);
        if (files.length === 0) {
          fetchFiles(0);
          setPage(0);
          setSearch("");
          setSearchInput("");
        }
      }
    });

    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [modalRef, files.length]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
    fetchFiles(0, searchInput || undefined);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchFiles(newPage * PAGE_SIZE, search || undefined);
  };

  const totalPages = Math.ceil(meta.count / PAGE_SIZE);

  return (
    <dialog ref={modalRef} className="modal">
      {selectedItem ? (
        <DetailViewShell
          onBack={() => {
            setSelectedItem(null);
            setStreamUrl(null);
          }}
          onClose={() => {
            setSelectedItem(null);
            setStreamUrl(null);
            modalRef.current?.close();
          }}
          title={selectedItem.fileName || "Untitled"}
          headerMeta={
            <>
              {selectedItem.source === "form_submission"
                ? selectedItem.parentLabel && (
                    <span>Form: {selectedItem.parentLabel}</span>
                  )
                : selectedItem.caseNumber && (
                    <span>Case: {selectedItem.caseId}</span>
                  )}
              <span>
                {new Date(selectedItem.createdAt).toLocaleDateString()}{" "}
                {new Date(selectedItem.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {selectedItem.metadata.duration && (
                <span>{formatDuration(selectedItem.metadata.duration)}</span>
              )}
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  selectedItem.metadata.audioType === "interaction"
                    ? "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400"
                    : "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                }`}
              >
                {selectedItem.metadata.audioType === "interaction"
                  ? "Interaction"
                  : "Narrative"}
              </span>
            </>
          }
          media={
            <MediaPlayer
              src={streamUrl}
              loading={streamLoading}
              isVideo={selectedItem.fileType?.startsWith("video/")}
            />
          }
          sidebar={
            <div className="space-y-3 text-sm">
              <DetailField label="Audio Type">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    selectedItem.metadata.audioType === "interaction"
                      ? "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400"
                      : "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                  }`}
                >
                  {selectedItem.metadata.audioType === "interaction"
                    ? "Interaction"
                    : "Narrative"}
                </span>
              </DetailField>
              {selectedItem.source === "form_submission"
                ? selectedItem.parentLabel && (
                    <DetailField label="Original Form">
                      <p className="text-xs text-gray-700 dark:text-base-content/80">
                        {selectedItem.parentLabel}
                      </p>
                    </DetailField>
                  )
                : selectedItem.caseNumber && (
                    <DetailField label="Original Case">
                      <p className="text-xs text-gray-700 dark:text-base-content/80">
                        {selectedItem.caseId}
                      </p>
                    </DetailField>
                  )}
              <DetailField label="File Size">
                <p className="text-xs text-gray-700 dark:text-base-content/80">{selectedItem.fileSize}</p>
              </DetailField>
              {selectedItem.metadata.duration && (
                <DetailField label="Duration">
                  <p className="text-xs text-gray-700 dark:text-base-content/80">
                    {formatDuration(selectedItem.metadata.duration)}
                  </p>
                </DetailField>
              )}
              <DetailField label="Uploaded">
                <p className="text-xs text-gray-700 dark:text-base-content/80">
                  {new Date(selectedItem.createdAt).toLocaleDateString()}{" "}
                  {new Date(selectedItem.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </DetailField>
              {selectedItem.metadata.speakers &&
                selectedItem.metadata.speakers.length > 0 && (
                  <DetailField label="Speakers">
                    <div className="space-y-1">
                      {selectedItem.metadata.speakers.map((s, i) => (
                        <p key={i} className="text-xs text-gray-700 dark:text-base-content/80">
                          {s.name}
                          {s.role && (
                            <span className="text-base-content/40"> ({s.role})</span>
                          )}
                        </p>
                      ))}
                    </div>
                  </DetailField>
                )}
              {selectedItem.metadata.context && (
                <DetailField label="Context">
                  <p className="text-xs text-gray-700 dark:text-base-content/80 whitespace-pre-wrap">
                    {selectedItem.metadata.context}
                  </p>
                </DetailField>
              )}
              {selectedItem.metadata.transcriptionUrl && (
                <div className="flex items-center gap-1.5">
                  <span className="px-2 py-0.5 bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 rounded text-xs font-medium">
                    Transcription available
                  </span>
                </div>
              )}
            </div>
          }
          footerLeft={
            <>
              {selectedItem.fileSize} &middot; {selectedItem.fileType}
            </>
          }
          footerRight={
            <Button
              onClick={() => {
                onSelect(selectedItem);
                setSelectedItem(null);
                setStreamUrl(null);
                modalRef.current?.close();
              }}
              disabled={selectedFileIds.includes(selectedItem.id)}
              variant="primary"
              size="sm"
            >
              {selectedFileIds.includes(selectedItem.id) ? "Already added" : "Add to Upload"}
            </Button>
          }
        />
      ) : (
        <ListViewShell
          title="Previous Uploads"
          subtitle="Select a previously uploaded audio file to reuse"
          onClose={() => modalRef.current?.close()}
        >
          <SearchBar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            onSearch={handleSearch}
            placeholder="Search by file name..."
          />

          <div className="flex-1 overflow-y-auto min-h-0">
            <ModalListStates
              loading={loading}
              error={error}
              isEmpty={files.length === 0}
              emptyMessage="No previous uploads found."
            />

            {!loading && !error && files.length > 0 && (
              <div className="space-y-2">
                {files.map((item) => {
                  const alreadySelected = selectedFileIds.includes(item.id);
                  return (
                    <ListItemButton
                      key={item.id}
                      disabled={alreadySelected}
                      onClick={() => handleSelectItem(item)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {item.fileType?.startsWith("video/") ? (
                            <VideoCameraIcon className="w-5 h-5 text-blue-500" />
                          ) : (
                            <MicrophoneIcon
                              className={`w-5 h-5 ${
                                item.metadata.audioType === "interaction"
                                  ? "text-purple-500"
                                  : "text-blue-500"
                              }`}
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-base-content truncate">
                              {item.fileName || "Untitled"}
                            </h4>
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                                item.metadata.audioType === "interaction"
                                  ? "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400"
                                  : "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                              }`}
                            >
                              {item.metadata.audioType === "interaction"
                                ? "Interaction"
                                : "Narrative"}
                            </span>
                            {alreadySelected && (
                              <span className="text-xs text-green-600 dark:text-green-400 flex-shrink-0 font-medium">
                                Added
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-base-content/60">
                            <span>
                              {new Date(item.createdAt).toLocaleDateString()}{" "}
                              {new Date(item.createdAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            {item.metadata.duration && (
                              <span>
                                {formatDuration(item.metadata.duration)}
                              </span>
                            )}
                            <span>{item.fileSize}</span>
                            {item.source === "form_submission" ? (
                              <span
                                className="px-1.5 py-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400 rounded text-xs truncate max-w-[320px]"
                                title={item.parentLabel || item.caseNumber}
                              >
                                Form: {item.parentLabel || item.caseNumber}
                              </span>
                            ) : (
                              item.caseNumber && (
                                <span
                                  className="px-1.5 py-0.5 bg-gray-100 dark:bg-base-200 rounded text-xs truncate max-w-[320px]"
                                  title={item.caseId}
                                >
                                  Case: {item.caseId}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </ListItemButton>
                  );
                })}
              </div>
            )}
          </div>

          {!loading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalCount={meta.count}
              onPageChange={handlePageChange}
            />
          )}
        </ListViewShell>
      )}
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );
};

export default PreviousUploadsModal;
